// This file deals with Marketplace API which will create Stripe Custom Connect accounts
// from given bank_account tokens.
import * as log from '../util/log';
import { storableError } from '../util/errors';

// ================ Action types ================ //

export const STRIPE_ACCOUNT_CREATE_REQUEST = 'app/stripe/STRIPE_ACCOUNT_CREATE_REQUEST';
export const STRIPE_ACCOUNT_CREATE_SUCCESS = 'app/stripe/STRIPE_ACCOUNT_CREATE_SUCCESS';
export const STRIPE_ACCOUNT_CREATE_ERROR = 'app/stripe/STRIPE_ACCOUNT_CREATE_ERROR';

export const STRIPE_ACCOUNT_UPDATE_REQUEST = 'app/stripe/STRIPE_ACCOUNT_UPDATE_REQUEST';
export const STRIPE_ACCOUNT_UPDATE_SUCCESS = 'app/stripe/STRIPE_ACCOUNT_UPDATE_SUCCESS';
export const STRIPE_ACCOUNT_UPDATE_ERROR = 'app/stripe/STRIPE_ACCOUNT_UPDATE_ERROR';

export const STRIPE_ACCOUNT_FETCH_REQUEST = 'app/stripe/STRIPE_ACCOUNT_FETCH_REQUEST';
export const STRIPE_ACCOUNT_FETCH_SUCCESS = 'app/stripe/STRIPE_ACCOUNT_FETCH_SUCCESS';
export const STRIPE_ACCOUNT_FETCH_ERROR = 'app/stripe/STRIPE_ACCOUNT_FETCH_ERROR';

export const STRIPE_ACCOUNT_CLEAR_ERROR = 'app/stripe/STRIPE_ACCOUNT_CLEAR_ERROR';

export const GET_ACCOUNT_LINK_REQUEST = 'app/stripeConnectAccount.duck.js/GET_ACCOUNT_LINK_REQUEST';
export const GET_ACCOUNT_LINK_SUCCESS = 'app/stripeConnectAccount.duck.js/GET_ACCOUNT_LINK_SUCCESS';
export const GET_ACCOUNT_LINK_ERROR = 'app/stripeConnectAccount.duck.js/GET_ACCOUNT_LINK_ERROR';

// ================ Reducer ================ //

const initialState = {
  createStripeAccountInProgress: false,
  createStripeAccountError: null,
  updateStripeAccountInProgress: false,
  updateStripeAccountError: null,
  fetchStripeAccountInProgress: false,
  fetchStripeAccountError: null,
  getAccountLinkInProgress: false,
  getAccountLinkError: null,
  stripeAccount: null,
  stripeAccountFetched: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case STRIPE_ACCOUNT_CREATE_REQUEST:
      return { ...state, createStripeAccountError: null, createStripeAccountInProgress: true };
    case STRIPE_ACCOUNT_CREATE_SUCCESS:
      return {
        ...state,
        createStripeAccountInProgress: false,
        stripeAccount: payload,
        stripeAccountFetched: true,
      };
    case STRIPE_ACCOUNT_CREATE_ERROR:
      console.error(payload);
      return { ...state, createStripeAccountError: payload, createStripeAccountInProgress: false };

    case STRIPE_ACCOUNT_UPDATE_REQUEST:
      return { ...state, updateStripeAccountError: null, updateStripeAccountInProgress: true };
    case STRIPE_ACCOUNT_UPDATE_SUCCESS:
      return {
        ...state,
        updateStripeAccountInProgress: false,
        stripeAccount: payload,
        stripeAccountFetched: true,
      };
    case STRIPE_ACCOUNT_UPDATE_ERROR:
      console.error(payload);
      return { ...state, updateStripeAccountError: payload, createStripeAccountInProgress: false };

    case STRIPE_ACCOUNT_FETCH_REQUEST:
      return { ...state, fetchStripeAccountError: null, fetchStripeAccountInProgress: true };
    case STRIPE_ACCOUNT_FETCH_SUCCESS:
      return {
        ...state,
        fetchStripeAccountInProgress: false,
        stripeAccount: payload,
        stripeAccountFetched: true,
      };
    case STRIPE_ACCOUNT_FETCH_ERROR:
      console.error(payload);
      return { ...state, fetchStripeAccountError: payload, fetchStripeAccountInProgress: false };

    case STRIPE_ACCOUNT_CLEAR_ERROR:
      return { ...initialState };

    case GET_ACCOUNT_LINK_REQUEST:
      return { ...state, getAccountLinkError: null, getAccountLinkInProgress: true };
    case GET_ACCOUNT_LINK_ERROR:
      console.error(payload);
      return { ...state, getAccountLinkInProgress: false, getAccountLinkError: payload };
    case GET_ACCOUNT_LINK_SUCCESS:
      return { ...state, getAccountLinkInProgress: false };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const stripeAccountCreateRequest = () => ({ type: STRIPE_ACCOUNT_CREATE_REQUEST });

export const stripeAccountCreateSuccess = stripeAccount => ({
  type: STRIPE_ACCOUNT_CREATE_SUCCESS,
  payload: stripeAccount,
});

export const stripeAccountCreateError = e => ({
  type: STRIPE_ACCOUNT_CREATE_ERROR,
  payload: e,
  error: true,
});

export const stripeAccountUpdateRequest = () => ({ type: STRIPE_ACCOUNT_UPDATE_REQUEST });

export const stripeAccountUpdateSuccess = stripeAccount => ({
  type: STRIPE_ACCOUNT_UPDATE_SUCCESS,
  payload: stripeAccount,
});

export const stripeAccountUpdateError = e => ({
  type: STRIPE_ACCOUNT_UPDATE_ERROR,
  payload: e,
  error: true,
});

export const stripeAccountFetchRequest = () => ({ type: STRIPE_ACCOUNT_FETCH_REQUEST });

export const stripeAccountFetchSuccess = stripeAccount => ({
  type: STRIPE_ACCOUNT_FETCH_SUCCESS,
  payload: stripeAccount,
});

export const stripeAccountFetchError = e => ({
  type: STRIPE_ACCOUNT_FETCH_ERROR,
  payload: e,
  error: true,
});

export const stripeAccountClearError = () => ({
  type: STRIPE_ACCOUNT_CLEAR_ERROR,
});

export const getAccountLinkRequest = () => ({
  type: GET_ACCOUNT_LINK_REQUEST,
});
export const getAccountLinkError = e => ({
  type: GET_ACCOUNT_LINK_ERROR,
  payload: e,
  error: true,
});
export const getAccountLinkSuccess = () => ({
  type: GET_ACCOUNT_LINK_SUCCESS,
});

// ================ Thunks ================ //

export const createStripeAccount = params => (dispatch, getState, sdk) => {
  if (typeof window === 'undefined' || !window.Stripe) {
    throw new Error('Stripe must be loaded for submitting PayoutPreferences');
  }
  const {
    country,
    accountType,
    businessProfileMCC,
    businessProfileURL,
    stripePublishableKey,
  } = params;
  const stripe = window.Stripe(stripePublishableKey);

  // Capabilities are a collection of settings that can be requested for each provider.
  // What Capabilities are required determines what information Stripe requires to be
  // collected from the providers.
  // You can read more from here: https://stripe.com/docs/connect/capabilities-overview
  // Note: with default processes, both 'card_payments' and 'transfers' are required.
  const requestedCapabilities = ['card_payments', 'transfers'];

  const accountInfo = {
    business_type: accountType,
    tos_shown_and_accepted: true,
  };

  dispatch(stripeAccountCreateRequest());

  return stripe
    .createToken('account', accountInfo)
    .then(response => {
      console.log('Stripe createToken response:', response);
      
      // Validate response structure
      if (!response) {
        const error = new Error('Stripe createToken returned undefined response - this usually means invalid publishable key or Stripe Connect not enabled');
        error.code = 'stripe_response_undefined';
        throw error;
      }
      
      if (response.error) {
        console.error('Stripe createToken error:', response.error);
        const error = new Error(`Stripe token creation failed: ${response.error.message || 'Unknown Stripe error'}`);
        error.stripeError = response.error;
        error.code = response.error.code || 'stripe_token_error';
        throw error;
      }
      
      if (!response.token || !response.token.id) {
        const error = new Error('Stripe response missing token or token ID - this indicates a Stripe API issue');
        error.code = 'stripe_missing_token';
        error.response = response;
        throw error;
      }
      
      console.log('Stripe token created successfully:', response.token.id);
      const accountToken = response.token.id;
      
      return sdk.stripeAccount.create(
        {
          country,
          accountToken,
          requestedCapabilities,
          businessProfileMCC,
          businessProfileURL,
        },
        { expand: true }
      );
    })
    .then(response => {
      const stripeAccount = response.data.data;
      dispatch(stripeAccountCreateSuccess(stripeAccount));
      // Attempt to persist Stripe account id to profile for dynamic payouts
      try {
        const attrs = stripeAccount?.attributes || {};
        const acctId = attrs.stripeAccountId || attrs?.stripeAccount?.id || stripeAccount?.id;
        if (acctId && typeof acctId === 'string' && acctId.startsWith('acct_')) {
          // Store to protectedData for server-side lookup without exposing publicly
          return sdk.currentUser
            .updateProfile({ protectedData: { stripeAccountId: acctId } })
            .then(() => stripeAccount)
            .catch(err => {
              console.error('Failed to write stripeAccountId to protectedData', err);
              return stripeAccount;
            });
        }
      } catch (persistErr) {
        console.error('Error deriving Stripe account id from creation response', persistErr);
      }
      return stripeAccount;
    })
    .catch(err => {
      console.error('Stripe account creation failed - Raw error:', err);
      
      const e = storableError(err);
      dispatch(stripeAccountCreateError(e));
      
      // Enhanced error message extraction
      let stripeMessage = null;
      let errorDetails = {};
      
      // Check different error structures
      if (err?.error?.message) {
        stripeMessage = err.error.message;
        errorDetails.stripeErrorType = err.error.type;
        errorDetails.stripeErrorCode = err.error.code;
      } else if (e.apiErrors && e.apiErrors.length > 0 && e.apiErrors[0].meta) {
        stripeMessage = e.apiErrors[0].meta.stripeMessage;
      } else if (err?.message) {
        stripeMessage = err.message;
      } else if (typeof err === 'string') {
        stripeMessage = err;
      }
      
      // Log additional error context
      errorDetails.errorType = typeof err;
      errorDetails.hasApiErrors = !!(e.apiErrors && e.apiErrors.length > 0);
      errorDetails.originalError = err?.toString();
      
      console.error('Stripe error details:', {
        stripeMessage,
        errorDetails,
        fullError: err
      });
      
      log.error(e, 'create-stripe-account-failed', { 
        stripeMessage,
        ...errorDetails
      });
      throw e;
    });
};

// This function is used for updating the bank account token
// but could be expanded to other information as well.
//
// If the Stripe account has been created with account token,
// you need to use account token also to update the account.
// By default the account token will not be used.
// See API reference for more information:
// https://www.sharetribe.com/api-reference/?javascript#update-stripe-account
export const updateStripeAccount = (params, options = {}) => {
  return (dispatch, getState, sdk) => {
    dispatch(stripeAccountUpdateRequest());

    console.log('🔧 updateStripeAccount called with:', { params, options });

    return sdk.stripeAccount
      .update(
        {
          // For now, try minimal update to see what the API expects
          capabilities: ['card_payments', 'transfers'],
        },
        {
          // this is the second argument for query params
          expand: options.expand || true,
        }
      )
      .then((response) => {
        console.log('✅ updateStripeAccount success:', response);
        const stripeAccount = response.data.data;
        dispatch(stripeAccountUpdateSuccess(stripeAccount));
        return stripeAccount;
      })
      .catch((err) => {
        console.error('❌ updateStripeAccount error:', err);
        console.error('❌ updateStripeAccount error details:', {
          message: err.message,
          status: err.status,
          data: err.data,
          response: err.response?.data,
          config: err.config
        });
        
        // Log the actual API error response if available
        if (err.response?.data) {
          console.error('❌ API Error Response:', JSON.stringify(err.response.data, null, 2));
        }
        
        dispatch(stripeAccountUpdateError(err));
        throw err;
      });
  };
};


export const fetchStripeAccount = params => (dispatch, getState, sdk) => {
  dispatch(stripeAccountFetchRequest());

  return sdk.stripeAccount
    .fetch()
    .then(response => {
      const stripeAccount = response.data.data;
      dispatch(stripeAccountFetchSuccess(stripeAccount));
      return stripeAccount;
    })
    .catch(err => {
      const e = storableError(err);
      dispatch(stripeAccountFetchError(e));
      const stripeMessage =
        e.apiErrors && e.apiErrors.length > 0 && e.apiErrors[0].meta
          ? e.apiErrors[0].meta.stripeMessage
          : null;
      log.error(err, 'fetch-stripe-account-failed', { stripeMessage });
      throw e;
    });
};

export const getStripeConnectAccountLink = params => (dispatch, getState, sdk) => {
  const { failureURL, successURL, type } = params;
  dispatch(getAccountLinkRequest());

  // Read more from collection_options and verification updates from Stripe's Docs:
  // https://docs.stripe.com/connect/handle-verification-updates
  return sdk.stripeAccountLinks
    .create({
      failureURL,
      successURL,
      type,
      collectionOptions: {
        fields: 'currently_due',
        future_requirements: 'include',
      },
    })
    .then(async response => {
      const url = response.data.data.attributes.url;
      // Auto-seed: immediately persist acct_ to protectedData if missing so provider payouts route automatically
      try {
        const [acctRes, userRes] = await Promise.all([
          sdk.stripeAccount.fetch().catch(() => null),
          sdk.currentUser.show().catch(() => null),
        ]);
        const acctObj = acctRes?.data?.data;
        const acctId = acctObj?.attributes?.stripeAccountId || acctObj?.attributes?.stripeAccount?.id || acctObj?.id;
        const user = userRes?.data?.data;
        const protectedData = user?.attributes?.profile?.protectedData || {};
        const existing = protectedData?.stripeAccountId;
        if (acctId && typeof acctId === 'string' && acctId.startsWith('acct_') && acctId !== existing) {
          await sdk.currentUser.updateProfile({ protectedData: { ...protectedData, stripeAccountId: acctId } });
        }
      } catch (seedErr) {
        // Non-fatal: linkage will still be backfilled on next login/currentUser fetch
        console.error('[stripeConnectAccount] auto-seed at link creation failed', seedErr);
      }
      return url;
    })
    .catch(err => {
      const e = storableError(err);
      dispatch(getAccountLinkError(e));
      const stripeMessage =
        e.apiErrors && e.apiErrors.length > 0 && e.apiErrors[0].meta
          ? e.apiErrors[0].meta.stripeMessage
          : null;
      log.error(err, 'get-stripe-account-link-failed', { stripeMessage });
      throw e;
    });
};
